import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  adminApi,
  type Paginated,
  type AgentRole,
  type Team,
  type TeamSlot,
  type TeamSortField,
  type TeamTemplate,
  type WorkflowStepInput,
} from '../api/admin';
import { useAgentRoles, type RoleOption, type TypeOption } from '../lib/use-agent-roles';
import { DEFAULT_TEAM_ICON, teamLabel } from '../lib/team-icon';
import { DataListFrame, sortArrow, useListControls } from '../components/data-list';
import {
  WorkflowStepsEditor,
  draftFromStep,
  draftsToInput,
  type WorkflowDraft,
} from '../components/workflow-editor';

const SORT_OPTIONS: { value: TeamSortField; label: string }[] = [
  { value: 'sortOrder', label: '表示順' },
  { value: 'id', label: 'ID' },
  { value: 'name', label: 'チーム名' },
  { value: 'createdAt', label: '作成日' },
];

// 一覧の列幅。先頭はドラッグ用のつまみ（三本線）の列。
const GRID_COLS = '28px 1fr 140px 90px 360px';

// チーム = エージェント（人格）のグルーピング。役割スロット（枠）に各エージェントを割り当てて編成する。
//
// 起草（基本情報：名前・アイコン・説明）と「編成」（役割枠＋メンバー割当）は別操作に分離している：
//   - 新規作成は基本情報だけを登録 →（社長指示）作成後そのまま「編成」モーダルへ。
//   - 一覧の各行は「編集」（基本情報）と「編成」を別ボタンで開く。
// 編成モーダルは TOP のテンプレ・ドロップダウンで役割枠を一括流し込み（既存枠は置き換え）でき、
// 各枠にエージェントを割り当てる。直感的に「テンプレ選ぶ→枠が並ぶ→人を入れる」の流れ。
export function TeamsPage() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['teams'] });
    qc.invalidateQueries({ queryKey: ['agents'] });
  };

  // 既定は「表示順」（ドラッグ＆ドロップで決めた順）。旧キーの保存済み条件（既定 id 順）を
  // 引きずらないよう、キーに .v2 を付けて仕切り直している。
  const controls = useListControls<TeamSortField>('konecco.list.teams.v2', {
    q: '',
    sortBy: 'sortOrder',
    sortDir: 'asc',
    page: 1,
    pageSize: 20,
  });
  // 区分(type) = ドロップダウン絞り込み（マスタ）。'' は指定なし。
  const [typeFilter, setTypeFilter] = useState('');
  const teamsKey = [
    'teams',
    'search',
    controls.q,
    typeFilter,
    controls.sortBy,
    controls.sortDir,
    controls.page,
    controls.pageSize,
  ];
  const teamsQuery = useQuery({
    queryKey: teamsKey,
    queryFn: () =>
      adminApi.searchTeams({
        q: controls.q,
        type: typeFilter || undefined,
        sortBy: controls.sortBy,
        sortDir: controls.sortDir,
        page: controls.page,
        pageSize: controls.pageSize,
      }),
    placeholderData: keepPreviousData,
  });
  const agentsQuery = useQuery({ queryKey: ['agents'], queryFn: adminApi.listAllAgents });
  const templatesQuery = useQuery({
    queryKey: ['team-templates'],
    queryFn: adminApi.listTeamTemplates,
  });
  const agents = agentsQuery.data ?? [];
  const templates = templatesQuery.data ?? [];
  const teams = teamsQuery.data?.items ?? [];
  const total = teamsQuery.data?.total ?? 0;
  // 区分・役割の選択肢／ラベルはマスタ管理(#176)から供給する。役割は区分でカスケード。
  const { typeOptions, typeLabelOf, roleOptionsByType, roleLabelOf } = useAgentRoles();

  const [createOpen, setCreateOpen] = useState(false);
  // 基本情報の編集モーダルと編成モーダルは ID で保持し、表示するチームは常に最新の一覧から引き直す
  // （割当変更で invalidate されてもモーダル内が最新スロットに追従するように）。
  const [editId, setEditId] = useState<number | null>(null);
  const [composeId, setComposeId] = useState<number | null>(null);
  const [workflowId, setWorkflowId] = useState<number | null>(null);
  const editTeam = teams.find((t) => t.id === editId) ?? null;
  const composeTeam = teams.find((t) => t.id === composeId) ?? null;
  const workflowTeam = teams.find((t) => t.id === workflowId) ?? null;

  const createTeam = useMutation({
    mutationFn: adminApi.createTeam,
    onSuccess: (created) => {
      setCreateOpen(false);
      invalidate();
      // 新規作成後はそのまま「編成」へ。続けて役割構成とメンバーを入れられるように。
      setComposeId(created.id);
    },
  });
  const updateTeam = useMutation({
    mutationFn: (vars: { id: number; input: { name: string; icon: string | null; description: string | null } }) =>
      adminApi.updateTeam(vars.id, vars.input),
    onSuccess: () => {
      setEditId(null);
      invalidate();
    },
  });
  const deleteTeam = useMutation({
    mutationFn: adminApi.deleteTeam,
    onSuccess: () => {
      setEditId(null);
      setComposeId(null);
      setWorkflowId(null);
      invalidate();
    },
  });
  // ワークフロー工程（工程フロー図）の丸ごと保存。
  const saveWorkflow = useMutation({
    mutationFn: (vars: { teamId: number; steps: WorkflowStepInput[] }) =>
      adminApi.setTeamWorkflow(vars.teamId, vars.steps),
    onSuccess: () => {
      setWorkflowId(null);
      invalidate();
    },
  });
  const addSlot = useMutation({
    mutationFn: (vars: { teamId: number; role: AgentRole }) =>
      adminApi.addTeamSlot(vars.teamId, vars.role),
    onSuccess: invalidate,
  });
  const updateSlot = useMutation({
    mutationFn: (vars: {
      slotId: number;
      input: { agentId?: number | null; isLeader?: boolean };
    }) => adminApi.updateTeamSlot(vars.slotId, vars.input),
    onSuccess: invalidate,
  });
  const deleteSlot = useMutation({ mutationFn: adminApi.deleteTeamSlot, onSuccess: invalidate });
  // テンプレ流し込み：既存枠をテンプレの役割構成で全置換し、作成元テンプレ(sourceTemplate)も記録する。
  // 枠の置き換えと由来記録をバックエンド 1 トランザクションで行うので一覧のテンプレ列に反映される。
  const applyTemplate = useMutation({
    mutationFn: (vars: { team: Team; template: TeamTemplate }) =>
      adminApi.applyTeamTemplate(vars.team.id, vars.template.id),
    onSuccess: invalidate,
  });
  const slotBusy =
    addSlot.isPending || updateSlot.isPending || deleteSlot.isPending || applyTemplate.isPending;

  // ── 並べ替え（三本線をつかんでドラッグ＆ドロップ）──
  // 決めた順番はサーバに保存され、チャット（操作卓）のチーム列も同じ順で並ぶ。
  // 並べ替えは「表示順・昇順」で見ているときだけ（名前順などで掴めても意味が無いため）。
  const canReorder = controls.sortBy === 'sortOrder' && controls.sortDir === 'asc';
  const [dragId, setDragId] = useState<number | null>(null);
  // どの行の「上」「下」に入るか（行の上下どちら半分にカーソルがあるかで決まる）。
  const [dropAt, setDropAt] = useState<{ id: number; after: boolean } | null>(null);
  const reorder = useMutation({
    mutationFn: adminApi.setTeamOrder,
    onMutate: async (ids: number[]) => {
      await qc.cancelQueries({ queryKey: ['teams'] });
      const prev = qc.getQueryData<Paginated<Team>>(teamsKey);
      // 楽観更新：保存を待たずに新しい並びで見せる（失敗したら元に戻す）。
      qc.setQueryData<Paginated<Team>>(teamsKey, (old) => {
        if (!old) return old;
        const byId = new Map(old.items.map((t) => [t.id, t]));
        const moved = ids.map((id) => byId.get(id)).filter((t): t is Team => t != null);
        const rest = old.items.filter((t) => !ids.includes(t.id));
        return { ...old, items: [...moved, ...rest] };
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(teamsKey, ctx.prev);
    },
    // チャット側のチーム列も引き直す（listTeams と、DIR 割当経由の devices の両方を見ている）。
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  // ドラッグ中のチームを targetId の上/下（targetId=null は末尾）へ移した、このページの新しい並び。
  const orderAfterMove = (
    draggedId: number,
    targetId: number | null,
    after: boolean,
  ): number[] => {
    const ids = teams.map((t) => t.id).filter((id) => id !== draggedId);
    const idx = targetId == null ? -1 : ids.indexOf(targetId);
    const at = idx < 0 ? ids.length : idx + (after ? 1 : 0);
    ids.splice(at, 0, draggedId);
    return ids;
  };
  const onDropOn = (targetId: number | null, after: boolean) => {
    const draggedId = dragId;
    setDragId(null);
    setDropAt(null);
    if (draggedId == null || !canReorder) return;
    const ids = orderAfterMove(draggedId, targetId, after);
    // 位置が変わっていなければ何もしない（無駄な保存を避ける）。
    if (ids.every((id, i) => teams[i]?.id === id)) return;
    reorder.mutate(ids);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p className="k-muted" style={{ margin: 0, fontSize: 13 }}>
        登録済みのチーム一覧です。「基本情報」で名前など、「チーム編成」で役割枠とメンバー割当、「ワークフロー」で各メンバーがどういうときにどう動くか（工程フロー）を設定できます。
        左の <span style={{ letterSpacing: 1 }}>☰</span> をつかんで上下にドラッグすると並び順を変えられ、その順番でチャットのチーム一覧にも並びます。
      </p>
      {teamsQuery.error && (
        <p className="k-error">取得に失敗しました: {(teamsQuery.error as Error).message}</p>
      )}
      {deleteTeam.error && (
        <p className="k-error">削除に失敗しました: {(deleteTeam.error as Error).message}</p>
      )}
      {reorder.error && (
        <p className="k-error">
          並び順の保存に失敗しました（表示は元に戻しました）: {(reorder.error as Error).message}
        </p>
      )}
      <DataListFrame
        title="チーム一覧"
        total={total}
        loading={teamsQuery.isLoading}
        controls={controls}
        sortOptions={SORT_OPTIONS}
        searchPlaceholder="あいまい検索…（チーム名・説明）"
        filters={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="k-muted" style={{ fontSize: 12 }}>
              区分
            </span>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                controls.setPage(1);
              }}
              style={{ padding: '7px 10px', fontSize: 13 }}
            >
              <option value="">すべて</option>
              {typeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        }
        rightActions={
          <button type="button" onClick={() => setCreateOpen(true)}>
            ＋ 新規作成
          </button>
        }
      >
        <div>
          {/* 一覧ヘッダ行（スクロール時も固定） */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: GRID_COLS,
              gap: 12,
              padding: '8px 14px',
              background: '#f5f7fa',
              fontSize: 12,
              fontWeight: 600,
              color: '#64748b',
              borderBottom: '1px solid #e7ebf0',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            {/* つまみ列の見出し＝「表示順」に戻すボタン。名前順などで見ていても1クリックで並べ替え可能に戻る。 */}
            <div
              onClick={() => {
                controls.setSortBy('sortOrder');
                controls.setSortDir('asc');
              }}
              style={{
                cursor: 'pointer',
                userSelect: 'none',
                textAlign: 'center',
                color: canReorder ? '#2563eb' : '#94a3b8',
              }}
              title={canReorder ? '表示順（ドラッグで並べ替え）' : 'クリックで表示順に戻す（ドラッグで並べ替えできるようになります）'}
            >
              ☰
            </div>
            <div
              onClick={() => controls.toggleSort('name')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="クリックで並び替え"
            >
              チーム名
              <span style={{ color: '#2563eb' }}>{sortArrow(controls, 'name')}</span>
            </div>
            <div>テンプレート</div>
            <div>メンバー</div>
            <div style={{ textAlign: 'right' }}>操作</div>
          </div>
          {/* 行の外側（一覧の余白）へのドロップも受ける＝末尾へ移動。 */}
          <div
            onDragOver={(e) => {
              if (dragId != null) e.preventDefault();
            }}
            onDrop={(e) => {
              e.preventDefault();
              onDropOn(null, true);
            }}
          >
            {teams.map((t, i) => (
              <TeamRow
                key={t.id}
                team={t}
                typeLabel={typeLabelOf(t.type)}
                last={i === teams.length - 1}
                draggable={canReorder}
                dragging={dragId === t.id}
                dropAt={dropAt?.id === t.id && dragId !== t.id ? (dropAt.after ? 'after' : 'before') : null}
                onDragStart={() => setDragId(t.id)}
                onDragEnd={() => {
                  setDragId(null);
                  setDropAt(null);
                }}
                onDragOverRow={(after) => setDropAt({ id: t.id, after })}
                onDropRow={(after) => onDropOn(t.id, after)}
                onEdit={() => setEditId(t.id)}
                onCompose={() => setComposeId(t.id)}
                onWorkflow={() => setWorkflowId(t.id)}
                onDelete={() => {
                  if (
                    confirm(
                      `チーム「${t.name}」を削除しますか？\n（所属エージェントは削除されず、所属だけ外れます）`,
                    )
                  ) {
                    deleteTeam.mutate(t.id);
                  }
                }}
              />
            ))}
          </div>
        </div>
      </DataListFrame>

      {createOpen && (
        <TeamCreateDialog
          typeOptions={typeOptions}
          typeLabelOf={typeLabelOf}
          roleLabelOf={roleLabelOf}
          templates={templates}
          submitting={createTeam.isPending}
          error={createTeam.error as Error | null}
          onClose={() => setCreateOpen(false)}
          onSubmit={(v) =>
            createTeam.mutate({
              name: v.name,
              type: v.type,
              icon: v.icon,
              description: v.description,
              templateId: v.templateId,
            })
          }
        />
      )}

      {editTeam && (
        <TeamEditDialog
          team={editTeam}
          saving={updateTeam.isPending}
          error={updateTeam.error as Error | null}
          onClose={() => setEditId(null)}
          onSave={(input) => updateTeam.mutate({ id: editTeam.id, input })}
        />
      )}

      {composeTeam && (
        <TeamComposeDialog
          team={composeTeam}
          agents={agents}
          templates={templates}
          roleOptions={roleOptionsByType(composeTeam.type)}
          roleLabelOf={roleLabelOf}
          typeLabelOf={typeLabelOf}
          slotBusy={slotBusy}
          applyError={applyTemplate.error as Error | null}
          onClose={() => setComposeId(null)}
          onApplyTemplate={(template) => applyTemplate.mutate({ team: composeTeam, template })}
          onAddSlot={(role) => addSlot.mutate({ teamId: composeTeam.id, role })}
          onAssign={(slotId, agentId) => updateSlot.mutate({ slotId, input: { agentId } })}
          onSetLeader={(slotId) => updateSlot.mutate({ slotId, input: { isLeader: true } })}
          onRemoveSlot={(slotId) => deleteSlot.mutate(slotId)}
        />
      )}

      {workflowTeam && (
        <TeamWorkflowDialog
          team={workflowTeam}
          roleOptions={roleOptionsByType(workflowTeam.type)}
          roleLabelOf={roleLabelOf}
          typeLabelOf={typeLabelOf}
          saving={saveWorkflow.isPending}
          error={saveWorkflow.error as Error | null}
          onClose={() => setWorkflowId(null)}
          onSave={(steps) => saveWorkflow.mutate({ teamId: workflowTeam.id, steps })}
        />
      )}
    </div>
  );
}

type AgentLite = { id: number; name: string; nickname: string | null; icon: string | null };

// 充足状況（割当済み枠 / 全枠）。一覧で「ちゃんと保存・編成されているか」が一目で分かるように。
// 所属＝役割枠への割当なので、充足は枠ベースのみで表す（枠なし所属は存在しない）。
function slotSummary(team: Team) {
  const slots = team.slots ?? [];
  const filled = slots.filter((s) => s.agentId != null).length;
  return { total: slots.length, filled };
}

// ドロップ位置の判定：行の下半分にカーソルがあれば「その行の下」に入れる（最下部へも動かせる）。
function isLowerHalf(e: DragEvent<HTMLDivElement>): boolean {
  const rect = e.currentTarget.getBoundingClientRect();
  return e.clientY > rect.top + rect.height / 2;
}

function TeamRow({
  team,
  typeLabel,
  last,
  draggable,
  dragging,
  dropAt,
  onDragStart,
  onDragEnd,
  onDragOverRow,
  onDropRow,
  onEdit,
  onCompose,
  onWorkflow,
  onDelete,
}: {
  team: Team;
  typeLabel: string;
  last: boolean;
  draggable: boolean; // 「表示順」で見ているときだけ掴める
  dragging: boolean; // 自分がドラッグ中
  dropAt: 'before' | 'after' | null; // ここに落としたとき、この行の上／下どちらに入るか
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOverRow: (after: boolean) => void;
  onDropRow: (after: boolean) => void;
  onEdit: () => void;
  onCompose: () => void;
  onWorkflow: () => void;
  onDelete: () => void;
}) {
  const { total, filled } = slotSummary(team);
  const workflowCount = team.workflowSteps?.length ?? 0;
  // ドラッグ中のゴースト画像に行全体を使う（つまみだけが飛ぶと掴んだ物が分かりにくいため）。
  const rowRef = useRef<HTMLDivElement | null>(null);
  return (
    <div
      ref={rowRef}
      onDragOver={(e) => {
        if (!draggable) return;
        e.preventDefault();
        onDragOverRow(isLowerHalf(e));
      }}
      onDrop={(e) => {
        if (!draggable) return;
        e.preventDefault();
        e.stopPropagation();
        onDropRow(isLowerHalf(e));
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: GRID_COLS,
        gap: 12,
        padding: '12px 14px',
        alignItems: 'center',
        background: '#fff',
        // 落下位置を上辺／下辺のラインで示す。ドラッグ中の行自体は薄く。
        borderTop: dropAt === 'before' ? '2px solid #2563eb' : '2px solid transparent',
        borderBottom:
          dropAt === 'after'
            ? '2px solid #2563eb'
            : last
              ? '2px solid transparent'
              : '1px solid #eef2f7',
        opacity: dragging ? 0.45 : 1,
      }}
    >
      {/* 並べ替えのつまみ（三本線）。ここだけドラッグできる（行内のボタンは普通に押せる）。 */}
      <div
        draggable={draggable}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          // Firefox はデータが無いと dragstart が成立しないので、識別子を入れておく。
          e.dataTransfer.setData('text/plain', String(team.id));
          if (rowRef.current) e.dataTransfer.setDragImage(rowRef.current, 24, 20);
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        aria-label={draggable ? 'ドラッグして並べ替え' : undefined}
        title={
          draggable
            ? 'ドラッグして並べ替え（この順番でチャットにも並びます）'
            : '並べ替えは「表示順」で見ているときだけできます（☰ の見出しをクリック）'
        }
        style={{
          color: draggable ? '#94a3b8' : '#e2e8f0',
          cursor: draggable ? 'grab' : 'not-allowed',
          fontSize: 15,
          lineHeight: 1,
          textAlign: 'center',
          userSelect: 'none',
        }}
      >
        ☰
      </div>

      {/* チーム名 */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>{team.icon || DEFAULT_TEAM_ICON}</span>
          <strong style={{ fontSize: 15 }}>{team.name}</strong>
          <span
            className="k-badge"
            title="区分"
            style={{ background: '#eef2ff', color: '#3730a3' }}
          >
            {typeLabel}
          </span>
        </div>
        {team.description && (
          <p
            className="k-muted"
            style={{
              margin: '2px 0 0 26px',
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {team.description}
          </p>
        )}
      </div>

      {/* テンプレート */}
      <div>
        {team.sourceTemplate ? (
          <span className="k-badge" title="作成元テンプレート">
            📋 {team.sourceTemplate.name}
          </span>
        ) : (
          <span className="k-muted" style={{ fontSize: 12 }}>
            —
          </span>
        )}
      </div>

      {/* メンバー充足 */}
      <div style={{ fontSize: 13 }}>
        {total === 0 ? (
          <span className="k-muted">枠なし</span>
        ) : (
          <span style={{ color: filled === total ? '#15803d' : '#475569' }}>
            👤 {filled}/{total}
          </span>
        )}
      </div>

      {/* 操作: 編集系3つをまとめ、破壊的な削除はアイコンで右端に分離する */}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
        <button className="btn-ghost" type="button" onClick={onEdit} style={rowBtn}>
          基本情報
        </button>
        <button type="button" onClick={onCompose} style={rowBtn}>
          チーム編成
        </button>
        <button
          className="btn-ghost"
          type="button"
          onClick={onWorkflow}
          title="工程フロー（誰がいつ何をするか）を設定"
          style={rowBtn}
        >
          ワークフロー
          {workflowCount > 0 && (
            <span
              style={{
                marginLeft: 5,
                padding: '0 6px',
                borderRadius: 9,
                background: '#ecfdf5',
                color: '#047857',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {workflowCount}
            </span>
          )}
        </button>
        <span style={{ width: 1, height: 20, background: '#e7ebf0', margin: '0 2px' }} />
        <button
          className="btn-danger"
          type="button"
          onClick={onDelete}
          title="チームを削除"
          aria-label="チームを削除"
          style={{ ...rowBtn, padding: '6px 9px' }}
        >
          🗑️
        </button>
      </div>
    </div>
  );
}

const labelStyle = { fontSize: 13, fontWeight: 600, color: '#334155' } as const;
const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 4 } as const;
// 一覧の操作ボタン共通スタイル。折り返さず（縦積み防止）、サイズを揃える。
const rowBtn = {
  fontSize: 13,
  padding: '6px 11px',
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
} as const;

// 基本情報（起草情報）編集モーダル：名前・アイコン・説明のみ。編成（メンバー割当）はここには無い。
function TeamEditDialog({
  team,
  saving,
  error,
  onClose,
  onSave,
}: {
  team: Team;
  saving: boolean;
  error: Error | null;
  onClose: () => void;
  onSave: (input: { name: string; icon: string | null; description: string | null }) => void;
}) {
  const [name, setName] = useState(team.name);
  const [icon, setIcon] = useState(team.icon ?? '');
  const [description, setDescription] = useState(team.description ?? '');

  const dirty =
    name.trim() !== team.name ||
    (icon.trim() || null) !== (team.icon ?? null) ||
    (description.trim() || null) !== (team.description ?? null);

  return (
    <Modal title={`基本情報を編集 — ${teamLabel(team)}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          onSave({
            name: name.trim(),
            icon: icon.trim() || null,
            description: description.trim() || null,
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ ...fieldWrap, width: 88 }}>
            <label style={labelStyle}>アイコン</label>
            <input
              placeholder="👥"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              style={{ textAlign: 'center', fontSize: 20 }}
              maxLength={4}
            />
          </div>
          <div style={{ ...fieldWrap, flex: 1 }}>
            <label style={labelStyle}>チーム名</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>説明・役割（任意）</label>
          <textarea
            placeholder="例: 本番運用の主力チーム。"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>
        {team.sourceTemplate && (
          <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>
            作成元テンプレート: 📋 {team.sourceTemplate.name}
          </p>
        )}
        {error && <p className="k-error">{error.message}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" disabled={!dirty || saving || !name.trim()}>
            {saving ? '保存中…' : '保存'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// 編成モーダル：TOP のテンプレ・ドロップダウンで役割枠を一括流し込み（既存枠は置換）→ 各枠に人を割り当てる。
function TeamComposeDialog({
  team,
  agents,
  templates,
  roleOptions,
  roleLabelOf,
  typeLabelOf,
  slotBusy,
  applyError,
  onClose,
  onApplyTemplate,
  onAddSlot,
  onAssign,
  onSetLeader,
  onRemoveSlot,
}: {
  team: Team;
  agents: AgentLite[];
  templates: TeamTemplate[];
  roleOptions: RoleOption[];
  roleLabelOf: (role: AgentRole) => string;
  typeLabelOf: (typeCode: string) => string;
  slotBusy: boolean;
  applyError: Error | null;
  onClose: () => void;
  onApplyTemplate: (template: TeamTemplate) => void;
  onAddSlot: (role: AgentRole) => void;
  onAssign: (slotId: number, agentId: number | null) => void;
  onSetLeader: (slotId: number) => void;
  onRemoveSlot: (slotId: number) => void;
}) {
  const [newRole, setNewRole] = useState<AgentRole | ''>('');
  // 役割枠追加の既定値はマスタ先頭の役割（選択肢は非同期取得なので空時はフォールバック）。
  const effectiveNewRole = newRole || roleOptions[0]?.value || '';

  const slots = team.slots ?? [];
  // 「区分→テンプレ」：このチームの区分に一致するテンプレートだけを流し込み候補にする。
  const typeTemplates = templates.filter((t) => t.type === team.type);

  const onPickTemplate = (templateId: number) => {
    const template = typeTemplates.find((t) => t.id === templateId);
    if (!template) return;
    const count = template.slots?.length ?? 0;
    if (
      slots.length > 0 &&
      !confirm(
        `現在の役割枠（${slots.length}枠）をテンプレート「${template.name}」の構成（${count}枠）に置き換えます。\n割り当て済みのメンバーは外れます。よろしいですか？`,
      )
    ) {
      return;
    }
    onApplyTemplate(template);
  };

  return (
    <Modal
      title={`チーム編成 — ${teamLabel(team)}`}
      onClose={onClose}
      width={600}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* TOP：区分の表示＋テンプレートから役割構成を流し込む（区分→テンプレ） */}
        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e7ebf0',
            borderRadius: 10,
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={labelStyle}>テンプレートから役割構成を入れる</label>
            <span className="k-badge" style={{ background: '#eef2ff', color: '#3730a3' }}>
              区分: {typeLabelOf(team.type)}
            </span>
          </div>
          <select
            value=""
            disabled={slotBusy || typeTemplates.length === 0}
            onChange={(e) => {
              if (e.target.value) onPickTemplate(Number(e.target.value));
              e.target.value = '';
            }}
            style={{ padding: '8px 10px', fontSize: 14 }}
          >
            <option value="">
              {typeTemplates.length === 0
                ? 'この区分のテンプレートはありません'
                : 'テンプレートを選択…'}
            </option>
            {typeTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.icon ? `${t.icon} ` : ''}
                {t.name}（{t.slots?.length ?? 0}枠）
              </option>
            ))}
          </select>
          <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>
            このチームの区分「{typeLabelOf(team.type)}」のテンプレートだけが出ます。選ぶと役割枠が一括でセットされ（今ある枠は置き換え）、あとは各枠に人を入れるだけ。
          </p>
          {applyError && <p className="k-error">{applyError.message}</p>}
        </div>

        {/* 役割スロット（メンバー割当） */}
        <div>
          <h4 style={{ margin: '0 0 4px' }}>役割枠とメンバー</h4>
          <p className="k-muted" style={{ fontSize: 12, margin: '0 0 10px' }}>
            役割枠ごとにエージェントを割り当てます。変更は即時保存されます。👑＝リーダー(PM)。チャットで宛先(@)を指定しないとき、この枠の人が応答します（チーム1人）。
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {slots.length === 0 ? (
              <p className="k-muted" style={{ fontSize: 13, margin: 0 }}>
                役割枠がありません。上のテンプレートから入れるか、下の「役割枠を追加」で1つずつ足してください。
              </p>
            ) : (
              slots.map((s) => (
                <SlotRow
                  key={s.id}
                  slot={s}
                  agents={agents}
                  roleLabelOf={roleLabelOf}
                  busy={slotBusy}
                  onAssign={(agentId) => onAssign(s.id, agentId)}
                  onSetLeader={() => onSetLeader(s.id)}
                  onRemove={() => onRemoveSlot(s.id)}
                />
              ))
            )}
          </div>

          {/* 役割枠の追加（個別） */}
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value={effectiveNewRole}
              disabled={roleOptions.length === 0}
              onChange={(e) => setNewRole(e.target.value as AgentRole)}
              style={{ padding: '6px 10px', fontSize: 13 }}
            >
              {roleOptions.length === 0 ? (
                <option value="">役割マスタがありません</option>
              ) : (
                roleOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))
              )}
            </select>
            <button
              className="btn-ghost"
              type="button"
              disabled={slotBusy || !effectiveNewRole}
              onClick={() => effectiveNewRole && onAddSlot(effectiveNewRole)}
            >
              ＋ 役割枠を追加
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose}>
            完了
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SlotRow({
  slot,
  agents,
  roleLabelOf,
  onAssign,
  onSetLeader,
  onRemove,
  busy,
}: {
  slot: TeamSlot;
  agents: AgentLite[];
  roleLabelOf: (role: AgentRole) => string;
  onAssign: (agentId: number | null) => void;
  onSetLeader: () => void;
  onRemove: () => void;
  busy: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 10px',
        background: slot.isLeader ? '#fffbeb' : '#fff',
        border: slot.isLeader ? '1px solid #fcd34d' : '1px solid #eef2f7',
        borderRadius: 8,
      }}
    >
      {/* 👑 リーダー(PM)トグル。1チーム1人（押すと他枠は自動でリーダー解除）。 */}
      <button
        type="button"
        onClick={onSetLeader}
        disabled={busy || slot.isLeader}
        aria-label={slot.isLeader ? 'リーダー(PM)' : 'リーダー(PM)に設定'}
        title={slot.isLeader ? 'このチームのリーダー(PM)' : 'クリックでリーダー(PM)に設定'}
        style={{
          border: 'none',
          background: 'transparent',
          cursor: busy || slot.isLeader ? 'default' : 'pointer',
          fontSize: 16,
          lineHeight: 1,
          padding: 2,
          opacity: slot.isLeader ? 1 : 0.3,
        }}
      >
        👑
      </button>
      <span
        className="k-badge"
        style={{ minWidth: 88, textAlign: 'center', background: '#eef2ff', color: '#3730a3' }}
      >
        {roleLabelOf(slot.role)}
      </span>
      <span className="k-muted" style={{ fontSize: 13 }}>
        →
      </span>
      <select
        value={slot.agentId ?? ''}
        disabled={busy}
        onChange={(e) => onAssign(e.target.value ? Number(e.target.value) : null)}
        style={{ flex: 1, padding: '6px 10px', fontSize: 13, maxWidth: 320 }}
      >
        <option value="">（未割当）</option>
        {agents.map((a) => (
          <option key={a.id} value={a.id}>
            {a.icon ? `${a.icon} ` : ''}
            {a.nickname || a.name}
          </option>
        ))}
      </select>
      <button
        className="btn-danger"
        type="button"
        onClick={onRemove}
        disabled={busy}
        aria-label="枠を削除"
        style={{ padding: '6px 9px' }}
      >
        ✕
      </button>
    </div>
  );
}

interface TeamCreateValues {
  name: string;
  type: string;
  icon: string | null;
  description: string | null;
  templateId: number | null;
}

// 新規作成ダイアログ：基本情報＋区分＋（任意で）テンプレートを登録する。
// テンプレを選ぶと、作成時にその役割構成・ワークフローがコピーされ、作成元テンプレも記録される
// （一覧のテンプレ列に出る）。テンプレ未選択でも作成でき、その場合は作成後の「編成」で役割を入れる。
function TeamCreateDialog({
  typeOptions,
  typeLabelOf,
  roleLabelOf,
  templates,
  onSubmit,
  onClose,
  submitting,
  error,
}: {
  typeOptions: TypeOption[];
  typeLabelOf: (typeCode: string) => string;
  roleLabelOf: (role: AgentRole) => string;
  templates: TeamTemplate[];
  onSubmit: (v: TeamCreateValues) => void;
  onClose: () => void;
  submitting: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState<string>('');
  // 既定アイコンを最初から入れておく（未入力のまま NULL で保存されるのを防ぐ）。消せば server 側の既定が入る。
  const [icon, setIcon] = useState(DEFAULT_TEAM_ICON);
  const [description, setDescription] = useState('');
  // 説明欄をユーザーが自分で触ったか。テンプレ既定の上書きは「未編集のとき」だけに限定し、入力済みは保護する。
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [templateId, setTemplateId] = useState<number | ''>('');
  // 区分はマスタ先頭を既定に（マスタ非同期取得のため空時はフォールバック）。
  const effectiveType = type || typeOptions[0]?.value || '';
  // 「区分→テンプレ」：選んだ区分に一致するテンプレだけを候補にする。
  const typeTemplates = templates.filter((t) => t.type === effectiveType);
  const selectedTemplate = templateId === '' ? null : templates.find((t) => t.id === templateId) ?? null;
  // テンプレ選択に連動して「説明」を初期設定する。未編集のときだけ反映（解除すると空に戻す）。役割枠は下にプレビュー表示。
  const onChangeTemplate = (nextId: number | '') => {
    setTemplateId(nextId);
    const tpl = nextId === '' ? null : templates.find((t) => t.id === nextId) ?? null;
    if (!descriptionTouched) setDescription(tpl?.description ?? '');
  };
  // 区分を変えたら、新区分に合わないテンプレ選択は外す（直感的に「区分を変えると合わないテンプレは消える」）。
  const onChangeType = (next: string) => {
    setType(next);
    if (templateId !== '' && !templates.some((t) => t.id === templateId && t.type === next)) {
      setTemplateId('');
      if (!descriptionTouched) setDescription('');
    }
  };

  return (
    <Modal title="新しいチームを作成" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim() || !effectiveType) return;
          onSubmit({
            name: name.trim(),
            type: effectiveType,
            icon: icon.trim() || null,
            description: description.trim() || null,
            templateId: templateId === '' ? null : templateId,
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ ...fieldWrap, width: 88 }}>
            <label style={labelStyle}>アイコン</label>
            <input
              placeholder="👥"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              style={{ textAlign: 'center', fontSize: 20 }}
              maxLength={4}
            />
          </div>
          <div style={{ ...fieldWrap, flex: 1 }}>
            <label style={labelStyle}>チーム名（例: ProdチームA）</label>
            <input
              placeholder="例: ProdチームA"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>区分（開発系／会社運用系…）</label>
          <select
            value={effectiveType}
            disabled={typeOptions.length === 0}
            onChange={(e) => onChangeType(e.target.value)}
          >
            {typeOptions.length === 0 ? (
              <option value="">区分マスタがありません</option>
            ) : (
              typeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))
            )}
          </select>
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            テンプレートと役割は、この区分のものだけが選べます。
          </p>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>テンプレート（任意）</label>
          <select
            value={templateId}
            disabled={typeTemplates.length === 0}
            onChange={(e) => onChangeTemplate(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">
              {typeTemplates.length === 0
                ? 'この区分のテンプレートはありません（あとで編成でも入れられます）'
                : 'テンプレートを使わない（あとで編成で入れる）'}
            </option>
            {typeTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.icon ? `${t.icon} ` : ''}
                {t.name}（{t.slots?.length ?? 0}枠）
              </option>
            ))}
          </select>
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            選ぶと作成と同時にこのテンプレの役割構成がコピーされ、一覧の「テンプレート」列にも出ます。区分「{typeLabelOf(effectiveType)}」のテンプレだけが候補です。
          </p>
          {selectedTemplate && (selectedTemplate.slots?.length ?? 0) > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {(selectedTemplate.slots ?? [])
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((s) => (
                  <span
                    key={s.id}
                    className="k-badge"
                    style={{ background: '#eef2ff', color: '#3730a3' }}
                  >
                    {roleLabelOf(s.role)}
                  </span>
                ))}
            </div>
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>説明・役割（任意）</label>
          <textarea
            placeholder="例: 本番運用の主力チーム。"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setDescriptionTouched(true);
            }}
            rows={2}
          />
          {selectedTemplate && !descriptionTouched && description && (
            <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
              テンプレ「{selectedTemplate.name}」の説明を初期値に入れました。自由に書き換えられます。
            </p>
          )}
        </div>
        <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>
          作成すると続けて「編成」が開きます。テンプレを選んだ場合は役割枠が入った状態で開くので、各枠にメンバーを割り当ててください（未選択なら編成でテンプレから入れられます）。
        </p>
        {error && <p className="k-error">{error.message}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" disabled={submitting || !name.trim() || !effectiveType}>
            作成して編成へ
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ワークフロー（工程フロー図）編集モーダル：このチームのメンバーが「どういうときにどう動くか」を
// 工程として順序付きで並べて保存する。役割枠の編成（メンバー割当）とは別操作。
function TeamWorkflowDialog({
  team,
  roleOptions,
  roleLabelOf,
  typeLabelOf,
  saving,
  error,
  onClose,
  onSave,
}: {
  team: Team;
  roleOptions: RoleOption[];
  roleLabelOf: (role: AgentRole) => string;
  typeLabelOf: (typeCode: string) => string;
  saving: boolean;
  error: Error | null;
  onClose: () => void;
  onSave: (steps: WorkflowStepInput[]) => void;
}) {
  const [steps, setSteps] = useState<WorkflowDraft[]>(
    () => (team.workflowSteps ?? []).map(draftFromStep),
  );

  return (
    <Modal title={`ワークフロー — ${teamLabel(team)}`} onClose={onClose} width={620}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e7ebf0',
            borderRadius: 10,
            padding: '10px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={labelStyle}>工程フロー（誰がいつ何をするか）</label>
            <span className="k-badge" style={{ background: '#eef2ff', color: '#3730a3' }}>
              区分: {typeLabelOf(team.type)}
            </span>
          </div>
          <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>
            上から順に流れる工程です。担当役割は、このチームの区分「{typeLabelOf(team.type)}」の役割から選べます。例: 設計者が設計を書く → MCP でタスク登録 → 完了後に実装者へ渡す → 質問が出たらチャットに報告。
          </p>
        </div>

        <WorkflowStepsEditor
          steps={steps}
          onChange={setSteps}
          roleOptions={roleOptions}
          roleLabelOf={roleLabelOf}
        />

        {error && <p className="k-error">{error.message}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="button" disabled={saving} onClick={() => onSave(draftsToInput(steps))}>
            {saving ? '保存中…' : 'ワークフローを保存'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
  width = 480,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '7vh 16px',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="k-card"
        style={{ width: '100%', maxWidth: width, maxHeight: '85vh', overflow: 'auto' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn-ghost" type="button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
